import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { Repeat, Sparkles, MapPin, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { shiftSwapApi } from "../services/schedulingApi";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Label } from "./ui/label";
import { useLocalization } from "../hooks/useLocalization";

export function ShiftSwapRequestModal({ shift, open, onClose, onSuccess }) {
  const { t } = useLocalization();
  const [mode, setMode] = useState("SPECIFIC");
  const [candidates, setCandidates] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [replacementId, setReplacementId] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !shift) return;
    setMode("SPECIFIC");
    setReplacementId("");
    setReason("");
    setLoadingCandidates(true);
    shiftSwapApi.eligibleReplacements(shift.id).then(({ data }) => setCandidates(data)).catch(() => setCandidates([])).finally(() => setLoadingCandidates(false));
  }, [open, shift]);

  if (!shift) return null;

  const handleSubmit = async () => {
    if (mode === "SPECIFIC" && !replacementId) {
      toast.error(t("shiftSwap.selectCoworkerError"));
      return;
    }
    if (!reason.trim()) {
      toast.error(t("shiftSwap.reasonRequiredError"));
      return;
    }
    setSubmitting(true);
    try {
      await shiftSwapApi.createRequest({
        assignment_id: shift.id, mode, replacement_employee_id: mode === "SPECIFIC" ? replacementId : null, reason: reason.trim(),
      });
      toast.success(t("shiftSwap.requestCreated"));
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("shiftSwap.requestCreateError"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="shift-swap-request-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Repeat size={18} /> {t("shiftSwap.requestModalTitle")}</DialogTitle>
        </DialogHeader>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-1">
          <p className="text-sm font-bold text-gray-900">{dayjs(shift.date).format("ddd, MMM D, YYYY")}</p>
          <p className="text-sm text-gray-600">{shift.start_time} - {shift.end_time}</p>
          <p className="text-xs text-gray-500 flex items-center gap-1"><MapPin size={12} /> {shift.store_location} {shift.role ? `· ${shift.role}` : ""}</p>
          {shift.is_qualifying_shift_template && (
            <div className="mt-1.5 flex items-start gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
              <Sparkles size={13} className="mt-0.5 flex-shrink-0" />
              <span data-testid="shift-swap-qualifying-disclaimer">{t("shiftSwap.qualifyingDisclaimer")}</span>
            </div>
          )}
        </div>

        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold text-gray-700">{t("shiftSwap.chooseModeLabel")}</Label>
            <RadioGroup value={mode} onValueChange={setMode} className="mt-1.5 space-y-2">
              <label className="flex items-start gap-2 border border-gray-200 rounded-lg p-2.5 cursor-pointer hover:bg-gray-50" data-testid="shift-swap-mode-specific">
                <RadioGroupItem value="SPECIFIC" className="mt-0.5" />
                <span className="text-sm"><span className="font-semibold text-gray-900">{t("shiftSwap.modeSpecificTitle")}</span><br /><span className="text-xs text-gray-500">{t("shiftSwap.modeSpecificDesc")}</span></span>
              </label>
              <label className="flex items-start gap-2 border border-gray-200 rounded-lg p-2.5 cursor-pointer hover:bg-gray-50" data-testid="shift-swap-mode-open">
                <RadioGroupItem value="OPEN" className="mt-0.5" />
                <span className="text-sm"><span className="font-semibold text-gray-900">{t("shiftSwap.modeOpenTitle")}</span><br /><span className="text-xs text-gray-500">{t("shiftSwap.modeOpenDesc")}</span></span>
              </label>
            </RadioGroup>
          </div>

          {mode === "SPECIFIC" && (
            <div>
              <Label className="text-xs font-semibold text-gray-700">{t("shiftSwap.coworkerLabel")}</Label>
              {loadingCandidates ? (
                <p className="text-xs text-gray-400 flex items-center gap-1.5 mt-1.5"><Loader2 size={13} className="animate-spin" /> {t("shiftSwap.loadingCoworkers")}</p>
              ) : candidates.length === 0 ? (
                <p className="text-xs text-red-500 mt-1.5" data-testid="shift-swap-no-eligible-coworkers">{t("shiftSwap.noEligibleCoworkers")}</p>
              ) : (
                <select
                  data-testid="shift-swap-coworker-select"
                  className="mt-1.5 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={replacementId}
                  onChange={(e) => setReplacementId(e.target.value)}
                >
                  <option value="">{t("shiftSwap.selectCoworkerPlaceholder")}</option>
                  {candidates.map((c) => (
                    <option key={c.employee_id} value={c.employee_id}>
                      {c.legal_name}{c.warnings.length > 0 ? ` (${t("shiftSwap.hasWarnings")})` : ""}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}
          {mode === "OPEN" && (
            <p className="text-xs text-gray-500" data-testid="shift-swap-open-eligible-count">{t("shiftSwap.openModeEligibleCount", { count: candidates.length })}</p>
          )}

          <div>
            <Label className="text-xs font-semibold text-gray-700">{t("shiftSwap.reasonLabel")}</Label>
            <Textarea data-testid="shift-swap-reason-input" className="mt-1.5" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("shiftSwap.reasonPlaceholder")} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="shift-swap-cancel-modal-btn">{t("common.cancel")}</Button>
          <Button onClick={handleSubmit} disabled={submitting || (mode === "SPECIFIC" && candidates.length === 0)} data-testid="shift-swap-submit-btn">
            {submitting ? t("shiftSwap.submitting") : t("shiftSwap.submitRequest")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
